// SkipJobsListPage — /skip/jobs.
//
// All-jobs listing matching the legacy `tricivo_system/operations/skips/
// jobs.php` layout. Filters by status / search; per-row "View" link
// to detail page.

import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { useSkipJobs } from '../hooks/useSkipJobs';
import { SkipJobStatusBadge } from './components/SkipJobStatusBadge';
import { JobRowActionsMenu } from './components/JobRowActionsMenu';
import jobMenuStyles from './components/JobRowActionsMenu.module.css';
import type { SkipJobSummary } from '../../../types/api';
import styles from './SkipJobsListPage.module.css';

// Feature 2 — quick-link subheader. Renders below PageHeader and
// above the filter card so frequently-used routes stay one click
// away regardless of which list page the staff member is on.
function SubheaderLinks(): JSX.Element {
  return (
    <nav className={styles.subheader} aria-label="Skip module quick links">
      <NavLink
        to="/skip/jobs/new"
        className={({ isActive }) =>
          [styles.subheaderLink, isActive ? styles.subheaderLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        Add Job
      </NavLink>
      <NavLink
        to="/skip/enquiries/create"
        className={({ isActive }) =>
          [styles.subheaderLink, isActive ? styles.subheaderLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        Add Enquiry
      </NavLink>
      <NavLink
        to="/skip/jobs/active"
        className={({ isActive }) =>
          [styles.subheaderLink, isActive ? styles.subheaderLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        Active Jobs
      </NavLink>
    </nav>
  );
}

function isTerminalStatus(status: string): boolean {
  return status === 'completed' || status === 'cancelled';
}

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'awaiting_collection', label: 'Awaiting Collection' },
  { value: 'collected', label: 'Collected' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function SkipJobsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  // One dropdown open at a time — track which row's enc_id has the
  // open Actions menu. null = no menu open.
  const [openActionsEncId, setOpenActionsEncId] = useState<string | null>(null);

  const filters: Record<string, string | number> = { page, limit: 25 };
  if (status) filters['status'] = status;
  if (q) filters['q'] = q;

  const jobs = useSkipJobs(filters);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Skip Jobs"
        subtitle="Manage all skip/bin/roro jobs"
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/skip/jobs/create')}>
            New Job
          </Button>
        }
      />

      <SubheaderLinks />

      <Card className={styles.filters}>
        <div className={styles.filterRow}>
          <input
            type="search"
            placeholder="Search by job number, customer, phone, postcode…"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className={styles.search}
            aria-label="Search jobs"
          />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className={styles.statusSelect}
            aria-label="Filter by status"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      <Card>
        {jobs.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : (jobs.data?.jobs.length ?? 0) === 0 ? (
          <p className={styles.empty}>No jobs match your filters.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Job #</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Postcode</th>
                <th>Delivery</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {jobs.data?.jobs.map((j: SkipJobSummary) => {
                const terminal = isTerminalStatus(j.status);
                return (
                  <tr key={j.enc_id}>
                    <td className={styles.cellMono}>
                      <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`}>{j.job_number}</Link>
                    </td>
                    <td>{j.customer_name}</td>
                    <td>{j.skip_stock_type_name ?? j.skip_type}</td>
                    <td>{j.customer_postcode}</td>
                    <td>{fmtDate(j.delivery_date)}</td>
                    <td>
                      <SkipJobStatusBadge status={j.status} />
                    </td>
                    <td className={styles.actions}>
                      <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`}>View</Link>
                      {terminal ? (
                        <span
                          className={jobMenuStyles.terminalBadge}
                          aria-label={`Job ${j.status}`}
                        >
                          {j.status}
                        </span>
                      ) : (
                        <JobRowActionsMenu
                          jobEncId={j.enc_id}
                          jobNumber={j.job_number}
                          customerName={j.customer_name}
                          skipSizeEncId={j.skip_size_enc_id}
                          isOpen={openActionsEncId === j.enc_id}
                          onToggle={() =>
                            setOpenActionsEncId((prev) =>
                              prev === j.enc_id ? null : j.enc_id,
                            )
                          }
                          onClose={() => setOpenActionsEncId(null)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {jobs.data && jobs.data.pagination.totalPages > 1 ? (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className={styles.pageInfo}>
              Page {page} of {jobs.data.pagination.totalPages} ({jobs.data.pagination.total} total)
            </span>
            <Button
              variant="secondary"
              disabled={page >= jobs.data.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
